/**
 * Thin wrapper around mp4-muxer for Wave's WebCodecs pipeline. Produces an
 * MP4 with H.264 video + AAC audio, moov-at-head so a browser can start
 * playing before the file is fully downloaded (the same guarantee ffmpeg's
 * `-movflags +faststart` gives us on the fallback path).
 *
 * The muxer runs in-memory: chunks are buffered until finalize() and then
 * returned as a single Blob. That's the right trade-off for Wave — target
 * files are ≤ a few hundred MB and finalize is a cheap byte-layout pass; a
 * fragmented mp4 buys nothing here and is less widely supported by the older
 * players Wave wants to keep working.
 *
 * See `project-transcode-backend` — this replaces the mpeg4 encoder for
 * WebCodecs-eligible sources.
 */

import { ArrayBufferTarget, FileSystemWritableFileStreamTarget, Muxer } from 'mp4-muxer';

/** AAC AudioSpecificConfig (2 bytes for LC + common sample rates). Wave
 *  always feeds mp4-muxer AAC-LC at the source's channel count and sample
 *  rate, so a 2-byte config is always enough. */
export interface AacConfig {
  sampleRate: number;
  numberOfChannels: number;
}

export interface OpenMuxerOptions {
  /** Video codec of the encoded chunks. `avc` = H.264, `hevc` = H.265;
   *  Wave picks the latter only when preserving HDR / 10-bit. */
  videoCodec?: 'avc' | 'hevc';
  width: number;
  height: number;
  /** Container-level rotation to write into the mp4 track header. */
  rotation?: 0 | 90 | 180 | 270;
  /** Nominal frame rate; muxer rounds sample timestamps against this when set. */
  frameRate?: number;
  /** Omit when the source has no audio track. */
  audio?: AacConfig;
  /** When set, the muxer streams output bytes to this writable file stream
   *  instead of buffering the whole MP4 in RAM. The caller obtains the
   *  stream via `showSaveFilePicker().createWritable()` — the user picks
   *  the destination up-front. `finalize()` still returns a Blob for API
   *  symmetry, but that Blob will be empty; the real file is on disk. Use
   *  this path for very large outputs (roughly > 500 MB) to avoid the
   *  4 GB array-buffer limit and reduce peak memory. */
  streamTarget?: FileSystemWritableFileStream;
}

export interface Mp4MuxHandle {
  /** Add an encoded H.264 chunk from a `VideoEncoder`. */
  addVideoChunk(chunk: EncodedVideoChunk, meta?: EncodedVideoChunkMetadata): void;
  /** Add a raw AAC access unit (ADTS payload minus the 7- or 9-byte header),
   *  with a timestamp / duration in microseconds. Wave uses this because our
   *  audio comes out of ffmpeg-wasm as an ADTS stream and we don't want to
   *  round-trip it through AudioDecoder + AudioEncoder just to synthesise
   *  an EncodedAudioChunk. */
  addRawAacFrame(payload: Uint8Array, timestampUs: number, durationUs: number): void;
  /** Flush and return the final MP4 as a Blob. Safe to call once. */
  finalize(): Blob;
}

const MIME_MP4 = 'video/mp4';

export function openMuxer(opts: OpenMuxerOptions): Mp4MuxHandle {
  const audio = opts.audio;

  // Two output shapes. In-memory: buffers everything, finalize() returns a
  // Blob. FSA-stream: writes progressively to the user's chosen file; the
  // 'fragmented' fastStart mode lets us do this without a giant final
  // rewrite — chunks land as they're produced, playable while still
  // growing (streaming-friendly), moov atoms interleaved as fragments.
  const streamTarget = opts.streamTarget;
  const bufferTarget = streamTarget ? null : new ArrayBufferTarget();
  const target = streamTarget
    ? new FileSystemWritableFileStreamTarget(streamTarget, { chunkSize: 4 * 1024 * 1024 })
    : bufferTarget!;
  const muxer = new Muxer({
    target,
    fastStart: streamTarget ? 'fragmented' : 'in-memory',
    firstTimestampBehavior: 'offset',
    video: {
      codec: opts.videoCodec ?? 'avc',
      width: opts.width,
      height: opts.height,
      ...(opts.rotation ? { rotation: opts.rotation } : {}),
      ...(opts.frameRate ? { frameRate: opts.frameRate } : {}),
    },
    ...(audio
      ? {
          audio: {
            codec: 'aac',
            numberOfChannels: audio.numberOfChannels,
            sampleRate: audio.sampleRate,
          },
        }
      : {}),
  });

  const audioDescription = audio ? buildAacDescription(audio) : null;
  let finalized = false;

  return {
    addVideoChunk(chunk, meta) {
      if (finalized) throw new Error('mp4-mux: addVideoChunk after finalize');
      muxer.addVideoChunk(chunk, meta);
    },
    addRawAacFrame(payload, timestampUs, durationUs) {
      if (finalized) throw new Error('mp4-mux: addRawAacFrame after finalize');
      if (!audioDescription) throw new Error('mp4-mux: audio track was not declared');
      muxer.addAudioChunkRaw(
        payload,
        'key', // every AAC access unit is a keyframe
        timestampUs,
        durationUs,
        { decoderConfig: { description: audioDescription } as unknown as AudioDecoderConfig }
      );
    },
    finalize() {
      if (finalized) throw new Error('mp4-mux: already finalized');
      finalized = true;
      muxer.finalize();
      if (bufferTarget) {
        return new Blob([bufferTarget.buffer], { type: MIME_MP4 });
      }
      // Streaming target: bytes already landed on the user's chosen file
      // handle. Return an empty Blob so the caller's API shape stays the
      // same; the real output is on disk. Caller must close the writable
      // stream themselves — the muxer doesn't own it.
      return new Blob([], { type: MIME_MP4 });
    },
  };
}

/** Build a 2-byte AudioSpecificConfig for AAC-LC at the given rate/channels.
 *  Layout: 5-bit objectType (2 = LC) · 4-bit samplingFrequencyIndex · 4-bit
 *  channelConfig · 3-bit GASpecificConfig (all zeros). */
export function buildAacDescription(cfg: AacConfig): Uint8Array {
  const freqIdx = aacFreqIndex(cfg.sampleRate);
  if (freqIdx < 0) throw new Error(`mp4-mux: unsupported AAC sample rate ${cfg.sampleRate}`);
  const channelConfig = cfg.numberOfChannels;
  if (channelConfig < 1 || channelConfig > 7) {
    throw new Error(`mp4-mux: unsupported AAC channel count ${channelConfig}`);
  }
  const objectType = 2; // AAC-LC
  // Pack 5+4+4+3 = 16 bits into 2 bytes.
  const b0 = (objectType << 3) | (freqIdx >> 1);
  const b1 = ((freqIdx & 0x1) << 7) | (channelConfig << 3);
  return new Uint8Array([b0, b1]);
}

// Standard AAC sampling-frequency table (ISO/IEC 14496-3).
const AAC_FREQ_TABLE = [
  96000, 88200, 64000, 48000, 44100, 32000, 24000, 22050,
  16000, 12000, 11025, 8000,  7350,
];

function aacFreqIndex(rate: number): number {
  return AAC_FREQ_TABLE.indexOf(rate);
}
